import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Brain,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  HelpCircle,
  Activity,
  Zap,
  Award,
} from "lucide-react";

export default function WhatIsTmsTherapyArticlePage() {
  const publishedDate = "2024-05-15";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/05/what-is-tms-therapy.jpg";

  const faqs = [
    {
      q: "What is TMS therapy and how does it work?",
      a: "TMS (Transcranial Magnetic Stimulation) is an FDA-cleared, non-invasive outpatient treatment that uses focused magnetic pulses to stimulate underactive neurons in the brain's dorsolateral prefrontal cortex, restoring normal neuro-circuitry and mood regulation.",
    },
    {
      q: "Does TMS therapy hurt or require anesthesia?",
      a: "No. TMS requires no anesthesia, sedation, or surgery. Patients remain completely alert during 18-to-20 minute sessions, reading or relaxing, and can drive themselves home or back to work immediately afterward.",
    },
    {
      q: "Is TMS therapy covered by health insurance?",
      a: "Yes. Major commercial insurers—including BCBS, Aetna, Cigna, UnitedHealthcare, and Medicare—cover TMS for Major Depressive Disorder when antidepressant medications haven't provided adequate relief.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="What is TMS Therapy? Complete Guide & Clinical Insights | Neuro Wellness"
        description="Learn what Transcranial Magnetic Stimulation (TMS) therapy is, how it rewires mood circuits in the brain without medication side effects, and why it achieves high remission rates for depression."
        canonical="/what-is-tms-therapy"
        keywords="what is TMS therapy, transcranial magnetic stimulation, non-invasive depression treatment, TMS Coral Springs FL, medication free depression cure"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/what-is-tms-therapy#webpage",
                name: "What is TMS Therapy? Non-Invasive Brain Stimulation",
                url: "https://neurowellnesstms.com/what-is-tms-therapy",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Comprehensive guide detailing Transcranial Magnetic Stimulation (TMS), including physics of magnetic pulsing, neuroplasticity impact, clinical efficacy, and candidate criteria.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id": "https://neurowellnesstms.com/what-is-tms-therapy#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Zap className="h-4 w-4" />
                    FDA-Cleared Interventional Psychiatry
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated May 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  What is <span className="text-accent">TMS Therapy?</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Transcranial Magnetic Stimulation (TMS) is a non-invasive,
                  drug-free brain stimulation technology clinically proven to
                  deliver long-lasting relief from treatment-resistant
                  depression.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      Instant Screening
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Check TMS Coverage
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLINICAL DIRECTOR QUOTE */}
        <section className="bg-muted/30 border-y border-border py-8">
          <div className="container-px mx-auto max-w-4xl">
            <blockquote className="text-base sm:text-lg italic text-foreground/90 border-l-4 border-accent pl-4 py-1">
              “TMS therapy allows us to treat the physical brain circuitry
              responsible for depression directly, without inundating the
              bloodstream with chemical side effects like weight gain or
              fatigue.”
            </blockquote>
            <p className="mt-2 text-xs sm:text-sm font-semibold text-accent uppercase tracking-wider pl-5">
              — Dr. Daniel Pietras, MD, Medical Director At Neuro Wellness TMS
              Centers of America
            </p>
          </div>
        </section>

        {/* BREADCRUMBS & ARTICLE */}
        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                { name: "What is TMS Therapy", href: "/what-is-tms-therapy" },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                For millions of adults suffering from Major Depressive Disorder,
                standard antidepressant medications either fall short or create
                intolerable side effects.{" "}
                <strong>Transcranial Magnetic Stimulation (TMS)</strong>{" "}
                provides a groundbreaking alternative by utilizing targeted
                magnetic pulses to revitalize sluggish neural pathways.
              </p>

              <h2>How TMS Works: The Neurobiology</h2>
              <p>
                Depression is associated with decreased metabolic activity and
                reduced electrical firing in the{" "}
                <strong>dorsolateral prefrontal cortex (DLPFC)</strong>, the
                region responsible for mood regulation, executive function, and
                motivation.
              </p>
              <p>
                During a TMS session, an electromagnetic coil is positioned
                comfortably against the scalp. It delivers precise, MRI-strength
                magnetic pulses that pass harmlessly through the skull to induce
                micro-electrical currents in underlying brain tissue. This
                process stimulates neurotransmitter release (serotonin,
                norepinephrine, dopamine) and promotes long-term
                neuroplasticity.
              </p>

              <h2>Key Benefits of TMS Therapy</h2>
              <ul>
                <li>
                  <strong>100% Non-Invasive:</strong> No surgery, no anesthesia,
                  and no systemic blood circulation.
                </li>
                <li>
                  <strong>No Systemic Side Effects:</strong> Does not cause
                  weight gain, sexual dysfunction, dry mouth, or memory loss.
                </li>
                <li>
                  <strong>Outpatient Convenience:</strong> Sessions take under
                  20 minutes; drive home immediately afterward.
                </li>
                <li>
                  <strong>Proven Efficacy:</strong> Over 60–70% of patients
                  experience significant clinical response or complete
                  remission.
                </li>
              </ul>

              <h2>Standard TMS vs Accelerated SAINT® TMS</h2>
              <p>
                Traditional TMS involves daily 20-minute visits over 6 weeks (36
                sessions). Neuro Wellness also offers{" "}
                <Link
                  to="/saint"
                  className="text-primary hover:underline font-semibold"
                >
                  SAINT® Accelerated TMS
                </Link>
                , an accelerated 5-day protocol delivering 50 sessions in one
                concentrated week with up to 79–90% remission rates.
              </p>
            </article>

            {/* FAQ SECTION */}
            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RELATED RESOURCES */}
            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Explore More TMS Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link
                    to="/how-long-does-a-tms-treatment-last"
                    className="hover:underline"
                  >
                    → How Long Does a TMS Treatment Last?
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-tms-therapy-affects-the-brain"
                    className="hover:underline"
                  >
                    → How TMS Therapy Affects the Brain
                  </Link>
                </li>
                <li>
                  <Link
                    to="/what-can-tms-therapy-treat"
                    className="hover:underline"
                  >
                    → What Can TMS Therapy Treat?
                  </Link>
                </li>
                <li>
                  <Link to="/saint" className="hover:underline">
                    → SAINT® 5-Day Accelerated TMS Protocol
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
